import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Extension } from '@arcnet/contracts';
import { useSession, useHasWorkspace } from '../../lib/auth/session.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useBrowserPhone, type BrowserPhoneApi } from './useBrowserPhone.js';
import { useShift, type ShiftApi } from './useShift.js';

/**
 * The agent's phone and shift, for the whole portal (3 October 2026).
 *
 * They lived in the workspace page; once the workspace was in the console's frame, opening Call
 * history from the menu unmounted it -- signing the phone out, hanging up the call, and letting
 * the shift go quiet. Here they last as long as the person is signed in, the workspace is one view
 * of them, a bar in the header shows a call from any page, and any number can be dialled.
 *
 * The extension is the person's OWN (owner, 3 October 2026: "just use the agents primary
 * extension"): the one linked to them that takes calls in the browser. Only somebody without one
 * -- an admin trying the workspace -- chooses.
 */
interface PhoneContext {
  seat: Extension | null;
  /** Extensions this person could take calls on, when they have more than one to choose from. */
  choices: Extension[];
  loading: boolean;
  choose: (id: string | null) => void;
  phone: BrowserPhoneApi | null;
  shift: ShiftApi | null;
  /** Bumped when a call ends, so lists of calls refresh. */
  endedAt: number;
}

const Ctx = createContext<PhoneContext>({
  seat: null, choices: [], loading: false, choose: () => undefined, phone: null, shift: null, endedAt: 0,
});

export const usePhone = (): PhoneContext => useContext(Ctx);

const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };

export function PhoneProvider({ children }: { children: ReactNode }) {
  const tenantId = useSession((s) => s.activeTenantId);
  const userId = useSession((s) => s.session?.user.id ?? null);
  const has = useHasWorkspace();
  const seats = useExtensions(has ? tenantId : null, { limit: 50 });
  const usable = useMemo(() => (seats.data?.data ?? []).filter((e) => e.may_workspace !== false), [seats.data]);
  const own = usable.find((e) => e.user_id === userId) ?? null;
  const key = `arc.ws.seat.${tenantId ?? ''}`;
  const [chosen, setChosen] = useState<string | null>(() => read(key));
  const choose = useCallback((id: string | null) => {
    setChosen(id);
    try { if (id) localStorage.setItem(key, id); else localStorage.removeItem(key); } catch { /* private window */ }
  }, [key]);
  const seat = own ?? usable.find((e) => e.id === chosen) ?? (usable.length === 1 ? usable[0]! : null);

  const base = { seat, choices: own ? [] : usable, loading: has && seats.data === undefined, choose };
  if (!has || !tenantId || !seat) {
    return <Ctx.Provider value={{ ...base, phone: null, shift: null, endedAt: 0 }}>{children}</Ctx.Provider>;
  }
  return <SeatPhone key={`${tenantId}:${seat.id}`} tenantId={tenantId} base={base}>{children}</SeatPhone>;
}

function SeatPhone({ tenantId, base, children }: {
  tenantId: string; base: Omit<PhoneContext, 'phone' | 'shift' | 'endedAt'>; children: ReactNode;
}) {
  const seat = base.seat!;
  const [endedAt, setEndedAt] = useState(0);
  const phone = useBrowserPhone({ tenantId, extensionId: seat.id, onEnded: () => setEndedAt(Date.now()) });
  const shift = useShift(tenantId, seat.id, phone.call !== null);
  const value = useMemo(() => ({ ...base, phone, shift, endedAt }), [base, phone, shift, endedAt]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
